import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Search, X, Loader2, UserCircle, Activity, CalendarClock,
  Stethoscope, Building2, Ticket, Users, CornerDownLeft, Sparkles,
  ChevronRight, Clock, Shield, LayoutDashboard,
  UserPlus, FileText, PhoneCall, Settings, BarChart3,
  ClipboardList, Key, Tag, Banknote, CreditCard,
  Compass
} from 'lucide-react';

const GlobalSearch = ({ navItems = [] }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    leads: [],
    surgeries: [],
    appointments: [],
    doctors: [],
    hospitals: [],
    tickets: [],
    users: [],
    totalMatches: 0
  });
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // 🔒 User-scoped storage key for recent searches
  const getRecentSearchKey = () => (user?._id ? `doxez_recent_searches_${user._id}` : 'doxez_recent_searches_anon');

  const [recentSearches, setRecentSearches] = useState([]);

  // Load user-specific recent searches when user changes or modal opens
  useEffect(() => {
    try {
      const key = getRecentSearchKey();
      const stored = localStorage.getItem(key);
      setRecentSearches(stored ? JSON.parse(stored) : []);
    } catch {
      setRecentSearches([]);
    }
  }, [user?._id, isOpen]);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const modalRef = useRef(null);

  // Keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Click & Touch Outside to Dismiss Modal
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } else {
      setQuery('');
      setResults({
        leads: [],
        surgeries: [],
        appointments: [],
        doctors: [],
        hospitals: [],
        tickets: [],
        users: [],
        totalMatches: 0
      });
      setSelectedIndex(0);
      setActiveCategory('all');
    }
  }, [isOpen]);

  // Debounced API Search
  useEffect(() => {
    if (!query.trim()) {
      setResults({
        leads: [],
        surgeries: [],
        appointments: [],
        doctors: [],
        hospitals: [],
        tickets: [],
        users: [],
        totalMatches: 0
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query.trim())}`);
        if (res.data?.success) {
          setResults(res.data.data);
          setSelectedIndex(0);
        }
      } catch (err) {
        console.error('Global search fetch error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // 🧭 List of Pages Strictly Derived from current user's accessible navItems
  const allNavPages = useMemo(() => {
    const pages = [];
    const seenPaths = new Set();

    const getIconComponent = (label) => {
      const l = (label || '').toLowerCase();
      if (l.includes('dashboard')) return LayoutDashboard;
      if (l.includes('performance')) return BarChart3;
      if (l.includes('report')) return FileText;
      if (l.includes('lead') || l.includes('intake')) return ClipboardList;
      if (l.includes('appointment') || l.includes('consultation')) return CalendarClock;
      if (l.includes('surgery') || l.includes('surgical') || l.includes('case')) return Activity;
      if (l.includes('onboard') || l.includes('invite')) return UserPlus;
      if (l.includes('doctor') || l.includes('surgeon')) return Stethoscope;
      if (l.includes('hospital') || l.includes('clinic')) return Building2;
      if (l.includes('payout') || l.includes('disbursal') || l.includes('settlement')) return Banknote;
      if (l.includes('payment') || l.includes('fee')) return CreditCard;
      if (l.includes('otp') || l.includes('verify')) return Key;
      if (l.includes('user') || l.includes('staff')) return Users;
      if (l.includes('role') || l.includes('permission')) return Shield;
      if (l.includes('coupon')) return Tag;
      if (l.includes('support') || l.includes('ticket') || l.includes('help')) return PhoneCall;
      if (l.includes('setting') || l.includes('config') || l.includes('audit')) return Settings;
      return Compass;
    };

    const extractPages = (items, parentLabel = '') => {
      (items || []).forEach(item => {
        if (item.subItems && Array.isArray(item.subItems)) {
          extractPages(item.subItems, item.label);
        }
        if (item.path) {
          const pathKey = item.path.toLowerCase();
          if (!seenPaths.has(pathKey)) {
            seenPaths.add(pathKey);
            pages.push({
              label: item.label,
              path: item.path,
              parent: parentLabel,
              desc: parentLabel ? `${parentLabel} → ${item.label}` : `Navigate to ${item.label} page`,
              icon: getIconComponent(item.label)
            });
          }
        }
      });
    };

    // Only extract pages that the user actually has access to via navItems
    extractPages(navItems);

    // Profile page is universally accessible to logged in users
    if (!seenPaths.has('/profile')) {
      pages.push({
        label: 'My Account & Profile',
        path: '/profile',
        parent: 'Account',
        desc: 'User profile settings & preferences',
        icon: Settings
      });
    }

    return pages;
  }, [navItems]);

  // Match navigation pages against the search query
  const matchedPages = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return allNavPages.filter(page => {
      return (
        page.label.toLowerCase().includes(q) ||
        page.parent.toLowerCase().includes(q) ||
        page.desc.toLowerCase().includes(q) ||
        page.path.toLowerCase().includes(q)
      );
    });
  }, [query, allNavPages]);

  // Quick navigation items for when search input is empty
  const defaultQuickNavItems = useMemo(() => {
    return allNavPages.slice(0, 8);
  }, [allNavPages]);

  // Flattened searchable results for rendering & keyboard arrow navigation
  const flattenedResults = useMemo(() => {
    const list = [];

    // 1. Matched Navigation Pages
    if (activeCategory === 'all' || activeCategory === 'pages') {
      matchedPages.forEach(page => {
        list.push({
          type: 'navigation',
          id: page.path,
          title: page.label,
          subtitle: page.desc,
          meta: page.parent || 'Page',
          path: page.path,
          icon: page.icon,
          badge: 'Page'
        });
      });
    }

    // 2. Leads
    if (activeCategory === 'all' || activeCategory === 'leads') {
      (results.leads || []).forEach(lead => {
        list.push({
          type: 'lead',
          id: lead._id,
          title: lead.patientName || 'Unnamed Patient',
          subtitle: `${lead.patientPhone || 'No phone'} ${lead.city ? `• ${lead.city}` : ''} ${lead.treatmentRequired || lead.disease ? `• ${lead.treatmentRequired || lead.disease}` : ''}`,
          meta: lead.bookingId || `#${lead._id.slice(-6)}`,
          status: lead.status,
          path: `/surgeries/${lead._id}?leadId=${lead._id}`,
          icon: UserCircle,
          badge: 'Lead'
        });
      });
    }

    // 3. Surgeries
    if (activeCategory === 'all' || activeCategory === 'surgeries') {
      (results.surgeries || []).forEach(surg => {
        const patient = surg.lead?.patientName || 'Patient Case';
        const leadId = surg.lead?._id || surg.lead || surg._id;
        list.push({
          type: 'surgery',
          id: surg._id,
          title: surg.surgeryName || surg.surgeryType || 'Surgery Case',
          subtitle: `Patient: ${patient} ${surg.hospital?.hospitalName ? `• ${surg.hospital.hospitalName}` : ''}`,
          meta: surg.bookingId || `#${surg._id.slice(-6)}`,
          status: surg.status,
          path: `/surgeries/${surg._id}?leadId=${leadId}`,
          icon: Activity,
          badge: 'Surgery'
        });
      });
    }

    // 4. Appointments
    if (activeCategory === 'all' || activeCategory === 'appointments') {
      (results.appointments || []).forEach(apt => {
        const docUser = apt.appointmentDoctorId?.user || apt.doctorId?.user;
        const docName = docUser ? `Dr. ${docUser.firstName} ${docUser.lastName || ''}` : '';
        const dateStr = apt.appointmentDate ? new Date(apt.appointmentDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
        const leadId = apt.leadId?._id || apt.leadId;
        list.push({
          type: 'appointment',
          id: apt._id,
          title: apt.patientName || 'Appointment',
          subtitle: `${apt.patientPhone || ''} ${docName ? `• ${docName}` : ''} ${dateStr ? `• ${dateStr} ${apt.appointmentTime || ''}` : ''}`,
          meta: apt.bookingId || `#${apt._id.slice(-6)}`,
          status: apt.status,
          path: leadId ? `/surgeries/${leadId}?leadId=${leadId}` : `/appointments/${apt._id}`,
          icon: CalendarClock,
          badge: 'Appointment'
        });
      });
    }

    // 5. Doctors
    if (activeCategory === 'all' || activeCategory === 'doctors') {
      (results.doctors || []).forEach(doc => {
        const name = doc.user ? `Dr. ${doc.user.firstName} ${doc.user.lastName || ''}` : 'Doctor';
        list.push({
          type: 'doctor',
          id: doc._id,
          title: name,
          subtitle: `${doc.specialization || doc.doctorType || 'Specialist'} ${doc.workingHospital ? `• ${doc.workingHospital}` : ''} ${doc.user?.phone ? `• ${doc.user.phone}` : ''}`,
          meta: doc.registrationNumber ? `Reg: ${doc.registrationNumber}` : '',
          path: `/doctors/${doc._id}`,
          icon: Stethoscope,
          badge: 'Doctor'
        });
      });
    }

    // 6. Hospitals
    if (activeCategory === 'all' || activeCategory === 'hospitals') {
      (results.hospitals || []).forEach(hosp => {
        list.push({
          type: 'hospital',
          id: hosp._id,
          title: hosp.hospitalName || 'Hospital Partner',
          subtitle: `${hosp.address?.city ? `${hosp.address.city} • ` : ''}${hosp.contactPerson ? `Contact: ${hosp.contactPerson} • ` : ''}${hosp.phone || hosp.mailId || ''}`,
          meta: hosp.address?.state || 'Hospital',
          path: `/hospitals/${hosp._id}`,
          icon: Building2,
          badge: 'Hospital'
        });
      });
    }

    // 7. Support Tickets
    if (activeCategory === 'all' || activeCategory === 'tickets') {
      (results.tickets || []).forEach(t => {
        list.push({
          type: 'ticket',
          id: t._id,
          title: t.subject || `Ticket ${t.ticketNumber}`,
          subtitle: `${t.patientName ? `Patient: ${t.patientName} • ` : ''}Priority: ${t.priority || 'Normal'}`,
          meta: t.ticketNumber || `#${t._id.slice(-6)}`,
          status: t.status,
          path: `/support?ticketId=${t._id}`,
          icon: Ticket,
          badge: 'Support'
        });
      });
    }

    // 8. Staff / Users
    if (activeCategory === 'all' || activeCategory === 'users') {
      (results.users || []).forEach(u => {
        list.push({
          type: 'user',
          id: u._id,
          title: `${u.firstName} ${u.lastName || ''}`,
          subtitle: `${u.email || ''} ${u.phone ? `• ${u.phone}` : ''}`,
          meta: u.role?.toUpperCase(),
          status: u.isActive ? 'active' : 'inactive',
          path: `/users/${u._id}`,
          icon: Users,
          badge: 'Staff'
        });
      });
    }

    return list;
  }, [results, matchedPages, activeCategory]);

  const handleSelect = (item) => {
    if (!item) return;

    // Save recent search if it was a text query
    if (query.trim()) {
      const updated = [
        query.trim(),
        ...recentSearches.filter(s => s.toLowerCase() !== query.trim().toLowerCase())
      ].slice(0, 6);
      setRecentSearches(updated);
      try {
        localStorage.setItem(getRecentSearchKey(), JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to save recent search:', err);
      }
    }

    setIsOpen(false);
    navigate(item.path);
  };

  const handleClearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(getRecentSearchKey());
    } catch (err) {
      console.error('Failed to clear recent searches:', err);
    }
  };

  const handleKeyDownInInput = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % (flattenedResults.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + flattenedResults.length) % (flattenedResults.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flattenedResults[selectedIndex]) {
        handleSelect(flattenedResults[selectedIndex]);
      }
    }
  };

  const totalCalculatedMatches = (results.totalMatches || 0) + (matchedPages.length || 0);

  const categories = useMemo(() => {
    const list = [{ id: 'all', label: 'All', count: totalCalculatedMatches }];

    if (matchedPages.length > 0) {
      list.push({ id: 'pages', label: 'Pages & Menu', count: matchedPages.length });
    }
    if (results.leads?.length > 0) {
      list.push({ id: 'leads', label: 'Leads', count: results.leads.length });
    }
    if (results.surgeries?.length > 0) {
      list.push({ id: 'surgeries', label: 'Surgeries', count: results.surgeries.length });
    }
    if (results.appointments?.length > 0) {
      list.push({ id: 'appointments', label: 'Appointments', count: results.appointments.length });
    }
    if (results.doctors?.length > 0) {
      list.push({ id: 'doctors', label: 'Doctors', count: results.doctors.length });
    }
    if (results.hospitals?.length > 0) {
      list.push({ id: 'hospitals', label: 'Hospitals', count: results.hospitals.length });
    }
    if (results.tickets?.length > 0) {
      list.push({ id: 'tickets', label: 'Support', count: results.tickets.length });
    }
    if (results.users?.length > 0) {
      list.push({ id: 'users', label: 'Users', count: results.users.length });
    }
    return list;
  }, [totalCalculatedMatches, matchedPages.length, results]);

  const getStatusBadgeClass = (status) => {
    if (!status) return 'bg-slate-100 text-slate-600 border-slate-200';
    const s = status.toLowerCase();
    if (['completed', 'paid', 'confirmed', 'active'].includes(s)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (['new', 'draft', 'pending', 'pending_payment'].includes(s)) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (['cancelled', 'lost', 'not_interested', 'failed'].includes(s)) return 'bg-rose-50 text-rose-700 border-rose-200';
    return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  };

  return (
    <>
      {/* 🔍 Search Input Trigger Button in Top Header */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center justify-between gap-3 px-3.5 py-2 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 rounded-2xl text-slate-400 hover:text-slate-600 text-xs font-bold transition-all w-48 md:w-64 lg:w-80 shadow-2xs group cursor-pointer"
        title="Search (Ctrl+K or ⌘K)"
      >
        <div className="flex items-center gap-2 truncate">
          <Search className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors shrink-0" />
          <span className="truncate">Search CRM pages, leads, cases...</span>
        </div>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-black text-slate-400 bg-white border border-slate-200 rounded-md font-mono shrink-0 shadow-2xs">
          ⌘K
        </kbd>
      </button>

      {/* 🌟 Global Omnisearch Modal Backdrop */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[999999] bg-slate-950/60 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 md:pt-20 overflow-y-auto animate-in fade-in duration-150"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          {/* Modal Container */}
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden z-10 flex flex-col max-h-[85vh] animate-in zoom-in-95 slide-in-from-top-4 duration-200 my-auto sm:my-0"
          >
            {/* 🔎 Search Input Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-white">
              <Search className="w-5 h-5 text-indigo-600 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDownInInput}
                placeholder="Search sidebar pages, patients, booking ID..."
                className="w-full bg-transparent border-none outline-none text-slate-800 font-bold text-sm sm:text-base placeholder:text-slate-400 placeholder:font-medium"
              />
              {loading ? (
                <Loader2 className="w-5 h-5 text-indigo-500 animate-spin shrink-0" />
              ) : query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <kbd className="hidden sm:inline-flex px-2 py-0.5 text-[10px] font-black text-slate-400 bg-slate-100 border border-slate-200 rounded-lg font-mono">
                  ESC
                </kbd>
              )}
            </div>

            {/* 🏷️ Filter Tabs (when searching and results exist) */}
            {query.trim() && categories.length > 1 && (
              <div className="flex items-center gap-1.5 px-4 py-2 border-b border-slate-100 bg-slate-50/70 overflow-x-auto no-scrollbar">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-3 py-1 rounded-xl text-[11px] font-black tracking-tight transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${activeCategory === cat.id
                      ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200'
                      : 'bg-white text-slate-600 hover:bg-slate-200/60 border border-slate-200/60'
                      }`}
                  >
                    <span>{cat.label}</span>
                    {cat.count !== undefined && (
                      <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-mono font-bold ${activeCategory === cat.id ? 'bg-indigo-700/60 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {cat.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* 📜 Results List */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-1 divide-y divide-slate-50">
              {/* If Query is Empty -> Show Recent Searches & Quick Nav */}
              {!query.trim() ? (
                <div className="p-2 space-y-6">
                  {/* Recent Searches */}
                  {recentSearches.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between px-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> Recent Searches
                        </span>
                        <button
                          onClick={handleClearRecentSearches}
                          className="text-[10px] font-bold text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {recentSearches.map((s, idx) => (
                          <button
                            key={idx}
                            onClick={() => setQuery(s)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200/80 rounded-xl text-xs font-bold text-slate-600 transition-colors flex items-center gap-1.5 cursor-pointer"
                          >
                            <Search className="w-3 h-3 text-slate-400" />
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CRM Quick Navigation Shortcuts */}
                  <div className="space-y-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-indigo-500" /> CRM Pages & Quick Navigation
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {defaultQuickNavItems.map((item, idx) => {
                        const Icon = item.icon || Compass;
                        return (
                          <div
                            key={idx}
                            onClick={() => handleSelect(item)}
                            className="p-3 bg-slate-50/70 hover:bg-indigo-50/70 border border-slate-100 hover:border-indigo-200 rounded-2xl transition-all cursor-pointer group flex items-center gap-3"
                          >
                            <div className="w-9 h-9 rounded-xl bg-white border border-slate-200/60 shadow-2xs flex items-center justify-center text-slate-600 group-hover:text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs font-black text-slate-800 group-hover:text-indigo-600 transition-colors">
                                {item.label}
                              </h4>
                              <p className="text-[10px] font-bold text-slate-400 truncate">
                                {item.desc}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : flattenedResults.length === 0 && !loading ? (
                /* No Results Found */
                <div className="py-12 px-4 text-center space-y-3">
                  <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-2xl flex items-center justify-center mx-auto">
                    <Search className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-black text-slate-700">No CRM Records or Pages Found</h3>
                  <p className="text-xs font-medium text-slate-400 max-w-sm mx-auto">
                    We couldn't find any accessible pages or records matching "<span className="font-bold text-slate-700">{query}</span>".
                  </p>
                </div>
              ) : (
                /* Matched Search Results */
                flattenedResults.map((item, idx) => {
                  const Icon = item.icon || Compass;
                  const isSelected = selectedIndex === idx;
                  const isPage = item.type === 'navigation';

                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`p-3 rounded-2xl transition-all cursor-pointer flex items-center justify-between gap-3 group ${isSelected
                        ? 'bg-indigo-50/90 border border-indigo-200 shadow-sm'
                        : 'hover:bg-slate-50 border border-transparent'
                        }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${isSelected ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : isPage ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-slate-100 text-slate-600 border-slate-200/80'}`}>
                          <Icon className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="text-xs font-black text-slate-800 group-hover:text-indigo-600 transition-colors truncate">
                              {item.title}
                            </h4>
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${isPage ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                              {item.badge}
                            </span>
                            {item.status && (
                              <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${getStatusBadgeClass(item.status)}`}>
                                {item.status.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] font-medium text-slate-500 truncate">
                            {item.subtitle}
                          </p>
                        </div>
                      </div>

                      {item.meta && (
                        <span className="hidden sm:inline-block px-2 py-1 bg-white border border-slate-200 text-slate-500 text-[10px] font-bold font-mono rounded-lg shrink-0">
                          {item.meta}
                        </span>
                      )}

                      <div className={`shrink-0 p-1.5 rounded-lg ${isSelected ? 'text-indigo-600 bg-white shadow-2xs' : 'text-slate-300'}`}>
                        <CornerDownLeft className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ⌨️ Modal Footer / Shortcut Tips */}
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between text-[11px] font-bold text-slate-400">
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[9px]">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[9px]">↓</kbd> to navigate
                </span>
                <span className="inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[9px]">↵</kbd> to select
                </span>
              </div>
              <span className="text-[10px] font-semibold text-slate-400">
                Doxez Omnisearch
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;
